/**
 * Tile shown on Sponsor Financials when the sponsor's income clears
 * 125% × FPG — Part 7 (assets) and the household co-filer block are
 * auto-hidden, and this tile takes their place so the user
 * understands the page didn't just "lose" sections.
 *
 * Renders the dollar threshold + household size so the math is
 * visible and the user can sanity-check.
 */
import styles from './IncomeThresholdTile.module.css';
import type { IIncomeThresholdState } from './incomeThreshold';

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function IncomeThresholdSuccessTile(props: {
  state: IIncomeThresholdState;
}): JSX.Element {
  const { state } = props;
  const regionLabel = state.region === 'alaska'
    ? 'Alaska'
    : state.region === 'hawaii'
    ? 'Hawaii'
    : 'the 48 contiguous states';
  return (
    <div
      className={styles.tile}
      data-income-threshold-success
      role="status"
      aria-live="polite"
    >
      <div className={styles.tile__head}>
        <span className={styles.tile__icon} aria-hidden="true">
          ✓
        </span>
        <div>
          <p className={styles.tile__title}>
            Your income meets the I-864 requirement
          </p>
          <p className={styles.tile__subtitle}>
            For a household of {state.householdSize} in {regionLabel}, the
            federal threshold is{' '}
            <strong>{USD_FORMATTER.format(state.threshold)}</strong> (125% of
            poverty). You reported{' '}
            <strong>
              {USD_FORMATTER.format(
                Math.max(state.individualIncome, state.householdIncome),
              )}
            </strong>
            .
          </p>
        </div>
      </div>
      <p className={styles.tile__skip}>
        We've skipped <strong>Part 7 (Assets)</strong> and the household
        co-filer section — neither is required when your income alone clears
        the threshold.
      </p>
    </div>
  );
}
